import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {InventoryOverrideService} from '../../services/inventory-override.service';
import {Observable, of, Subscription} from 'rxjs';
import {ProfileSelectorService} from '../profile-selector/profile-selector.service';
import {DialogAction, DialogCloseResult, DialogRef, DialogService} from '@progress/kendo-angular-dialog';
import {OverrideType} from '@shared/enums';
import {BaseInventoryOverride, BulkConfirmChoice} from '../../models/base-inventory-override.model';
import {OverrideLevel} from '../../../../inventory/inventory.model';
import {
    VariableProfileSelectorService
} from "@shared/inventory-override/components/variable-profile-selector/variable-profile-selector.service";
import {FilterItem} from "@shared/filter-item.model";
import {map} from "rxjs/operators";
import {LookupService} from "@shared/lookup.service";
import {DateUtils} from "@shared/date-utils";
import {NotificationPopupService} from "@shared/notification-popup/services/notification-popup.service";

@Component({
    selector: 'app-bulk-inventory-override',
    templateUrl: './bulk-inventory-override.component.html',
    styleUrls: ['./bulk-inventory-override.component.scss']
})
export class BulkInventoryOverrideComponent extends BaseInventoryOverride implements OnInit, OnDestroy {
    // Without a width Kendo sizes these dialogs to their content, and the long message plus
    // three buttons stretch them across the whole page, behind the sidebar.
    private static readonly CONFIRM_DIALOG_SIZE = {width: 520, maxWidth: '90vw'};

    @Input() bulkRequest: any;
    @Input() notifyContainer!: ViewContainerRef;
    @ViewChild('rsidMultiSelect') private rsidMultiSelect: any;
    public loading = true;
    @Input() set parentPopupContainer(container: ViewContainerRef) {
        this.parentContainer = container;
        this.popupSettings = { appendTo: container };
    }

    public bulkBucketToggle = true;
    public popupSettings: any;
    private parentContainer: ViewContainerRef;
    private confirmDialog: DialogRef;
    public bulkStaticProfileToggle = false;
    public bulkVariableProfileToggle = false;
    public multiSelectRsidsFlag = false
    public multiSelectDatesFlag = false
    public selectedDates: Date[] = [];
    public minSelectableDate: Date;
    public maxSelectableDate: Date;

    public invOverride: any;
    public title = 'Bulk Override';
    private selectedProfile: any;
    private profileChangeSub: Subscription;
    public selectedSection: any = 1;
    public options = [
        {label: 'Bucket', value: 'bucket'},
        {label: 'Static Profile', value: 'staticProfile'},
        {label: 'Variable Profile', value: 'variableProfile'}
    ];

    public selectedOption: string;
    private variableProfileChangeValueType: any;
    private readonly variableProfileChangeType: Subscription;
    public rsidData: FilterItem = new FilterItem();

    public onOptionClick(value: string) {
        this.selectedOption = value;
        console.log(this.selectedSection);

        if (value === 'bucket') {
            this.bulkBucketToggle = true;
            this.bulkStaticProfileToggle = false;
            this.bulkVariableProfileToggle = false;
        } else if (value === 'staticProfile') {
            this.bulkBucketToggle = false;
            this.bulkStaticProfileToggle = true;
            this.bulkVariableProfileToggle = false;
        } else if (value === 'variableProfile') {
            this.bulkBucketToggle = false;
            this.bulkStaticProfileToggle = false;
            this.bulkVariableProfileToggle = true;
        }
    }

    public rsidValueNormalizer = (text$: Observable<string>) => text$.pipe(map((text: string) => {
        // search for matching item to avoid duplicates

        // search in values
        const matchingValue: any = this.rsidData.selectedValues.find((item: any) => {
            return item.name.toLowerCase() === text.toLowerCase();
        });

        if (matchingValue) {
            return matchingValue; // return the already selected matching value and the component will remove it
        }

        // search in data
        const matchingItem: any = this.rsidData.allValues.find((item: any) => {
            return item.name.toLowerCase() === text.toLowerCase();
        });

        if (matchingItem) {
            return matchingItem;
        } else {
            return {
                id: text,
                name: text
            };
        }
    }))

    handleFilterRSID(value){
        if (value.trim().length){
            // Split the values on both comma and space.
            const values = value.split(/[\s,]+/).map((x) => {return x.toUpperCase()})

            if (values.length > 1)
                this.rsidData.filterItemsByFn((s) => values.includes(s.id) , false)
            else
                this.rsidData.filterItemsByFn((s) =>s.id.toLowerCase().indexOf(value.toLowerCase()) !== -1, false)
        }else{
            this.rsidData.filteredValues = this.rsidData.allValues
        }
        this.cdr.detectChanges();
    }

    public rsidMultiSelectChange(): void {
        // TODO: Remove hack once KendoUI fixes bug with multi select
        // https://github.com/telerik/kendo-angular/issues/2753
        console.log(this.rsidData);
        this.invOverride.selectedRSIDS = this.rsidData.selectedValues.map((item: any) => item.id);
        console.log(this.invOverride);
        this.rsidMultiSelect.text = '';
        setTimeout(() => {
            this.rsidMultiSelect.focus();
        });
    }

    public dropdownTagMapper(tags: any[]): any[] {
        return tags.length < 3 ? tags : [tags];
    }

    public datesMultiSelectChange(): void {
        this.invOverride.selectedDates = this.selectedDates
            .map((selectedDate: Date) => DateUtils.convertDateToStringFormat(selectedDate));
    }

    constructor(public dialog: DialogRef,
                private overrideSvc: InventoryOverrideService,
                private profileSelectSvc: ProfileSelectorService,
                private variableProfileSelectSvc: VariableProfileSelectorService,
                private cdr: ChangeDetectorRef,
                private lookupSvc: LookupService,
                private notificationSvc: NotificationPopupService,
                private dialogService: DialogService) {
        super(dialog);
        this.selectedOption = 'bucket';
        this.profileChangeSub = this.profileSelectSvc.profileChange$.subscribe(this.selectedProfileChange.bind(this));
        this.variableProfileChangeType = this.variableProfileSelectSvc.variableProfileChangeType$.subscribe(this.variableProfileChange.bind(this));
        this.rsidData.loadAllValues(this.lookupSvc.loadRSIDs());
    }

    ngOnInit(): void {
        this.getOverrideInfo();
    }

    ngOnDestroy(): void {
        this.profileChangeSub.unsubscribe();
        // Otherwise a confirmation left open outlives its parent dialog, overlay and all.
        this.confirmDialog?.close();
    }

    public isValid(): boolean {
        if (this.bulkVariableProfileToggle) {
            return this.variableProfileChangeValueType !== undefined;
        } else {
            return !this.bulkBucketToggle ? this.selectedProfile : super.isValid();
        }
    }

    public isVariableProfileValid(): boolean {
        if (this.bulkVariableProfileToggle) {
            return this.variableProfileChangeValueType === undefined;
        }
        return false
    }

    public onApprove(): void {
        this.confirmBulkChange('Approve').subscribe((choice: BulkConfirmChoice) => {
            if (choice) {
                this.saveApprovals(choice === 'applyAndSend');
            }
        });
    }

    public onSave(): void {
        this.confirmBulkChange('Override').subscribe((choice: BulkConfirmChoice) => {
            if (choice) {
                this.saveOverrides(choice === 'applyAndSend');
            }
        });
    }

    // A bulk change fans out across every selected OD/RSID/date and auto-pilot sends it
    // to market unreviewed, so state the scope and make the user confirm it.
    //
    // Three-way rather than yes/no: the third option also queues the journeys to RARS
    // straight away instead of leaving them for the next scheduled export. Only Discard
    // (and X/Esc) abandons the change, matching the single override modal.
    private confirmBulkChange(actionLabel: string): Observable<BulkConfirmChoice> {
        // The action buttons sit outside the loading overlay, so they are clickable
        // before getBulkOverrideInfo has returned anything to act on.
        if (this.loading || !this.invOverride) {
            return of(null);
        }
        if (!this.selectedRsidCount() || !this.selectedDateCount()) {
            this.confirmDialog = this.dialogService.open({
                title: `Nothing to ${actionLabel}`,
                content: 'Select at least one RSID and one date before applying a bulk change.',
                ...BulkInventoryOverrideComponent.CONFIRM_DIALOG_SIZE,
                appendTo: this.parentContainer,
                actions: [{text: 'OK', primary: true}]
            });
            return this.confirmDialog.result.pipe(map((): BulkConfirmChoice => null));
        }

        const sendLabel = `${actionLabel} and Send`;
        this.confirmDialog = this.dialogService.open({
            title: `Confirm Bulk ${actionLabel}`,
            content: this.buildConfirmationMessage(actionLabel),
            ...BulkInventoryOverrideComponent.CONFIRM_DIALOG_SIZE,
            // Same container as the bulk dialog: equal z-index, so DOM order decides which paints on top.
            appendTo: this.parentContainer,
            actions: [
                {text: 'Discard'},
                // The plain action is primary: sending is the more consequential choice,
                // so it should not be what an absent-minded Enter key picks.
                {text: actionLabel, primary: true},
                {text: sendLabel}
            ]
        });
        return this.confirmDialog.result.pipe(
            map((result: any): BulkConfirmChoice => {
                if (result instanceof DialogCloseResult) {
                    return null;
                }
                switch ((result as DialogAction)?.text) {
                    case sendLabel:
                        return 'applyAndSend';
                    case actionLabel:
                        return 'apply';
                    default:
                        return null;
                }
            })
        );
    }

    private buildConfirmationMessage(actionLabel: string): string {
        const odCount = this.selectedOdCount();
        const rsidCount = this.selectedRsidCount();
        const dateCount = this.selectedDateCount();
        // Every OD is applied on every RSID/date, but not every RSID runs on every
        // selected date, so the product is an upper bound.
        const combinations = (odCount || 1) * rsidCount * dateCount;
        const scope = (odCount ? `${odCount} O&D pair${odCount === 1 ? '' : 's'}, ` : '')
            + `${rsidCount} RSID${rsidCount === 1 ? '' : 's'} and `
            + `${dateCount} date${dateCount === 1 ? '' : 's'}`;
        const tail = `${scope} (up to ${combinations} journey/date combination${combinations === 1 ? '' : 's'})?`;
        if (actionLabel === 'Approve') {
            // saveApprovals() forwards variableProfileChangeType/Value, so that edit really is
            // submitted - but it clears buckets and never reads the static profile.
            if (this.bulkVariableProfileToggle) {
                return `Approve the recommended values, shifted by the selected variable profile`
                    + ` change, for ${tail}`
                    + this.sendHint(actionLabel);
            }
            return `Approve the recommended values for ${tail}`
                + ` Any ${this.overrideTypeLabel()} changes made here will be discarded.`
                + this.sendHint(actionLabel);
        }
        return `Apply this ${this.overrideTypeLabel()} override to ${tail}`
            + this.sendHint(actionLabel);
    }

    // The send is asynchronous: the journeys are queued to the RARS publish pipeline,
    // they are not in the reservation system by the time this dialog closes.
    private sendHint(actionLabel: string): string {
        return ` Choosing ${actionLabel} and Send will also queue these journeys to RARS`
            + ' instead of waiting for the next scheduled export.';
    }

    // OD-level bulk applies to one journey pair per selected grid row; other levels have none.
    private selectedOdCount(): number {
        return this.countOf(this.invOverride?.journeyPairs);
    }

    private selectedRsidCount(): number {
        // selectedRSIDS is only populated once the multiselect changes; rsidData holds the initial RSID.
        const explicit = this.invOverride?.selectedRSIDS;
        return Array.isArray(explicit) ? explicit.length : this.countOf(this.rsidData?.selectedValues);
    }

    private selectedDateCount(): number {
        return this.countOf(this.invOverride?.selectedDates);
    }

    // An empty selection must read as 0, not fall through to a default of 1.
    private countOf(value: any): number {
        return Array.isArray(value) ? value.length : 0;
    }

    private overrideTypeLabel(): string {
        const option = this.options.find((item: any) => item.value === this.selectedOption);
        return option ? option.label.toLowerCase() : 'bulk';
    }

    private getOverrideInfo(): void {
        this.overrideSvc.getBulkOverrideInfo(this.bulkRequest).subscribe(resp => {
            this.loading = false;
            resp.overrideType = OverrideType.Bulk;
            resp.overrideLevel = this.bulkRequest.overrideLevel;
            resp.passengerClass = this.bulkRequest.passengerClass;
            if (this.bulkRequest.dataItem) {
                resp.origin = this.bulkRequest.dataItem.origin;
                resp.destination = this.bulkRequest.dataItem.destination;
            }

            if (resp.overrideLevel === 'od') {
                resp.journeyPairs = this.bulkRequest.journeyPairs;
            }
            this.invOverride = resp;
            this.rsidData.selectedValues = [{'id': this.invOverride.rsid, 'name': this.invOverride.rsid}];
            this.multiSelectRsidsFlag = this.invOverride.multiSelectRsidsFlag;
            this.selectedDates = [DateUtils.convertToDate(this.invOverride.departureDate)];
            this.invOverride.selectedDates = [this.invOverride.departureDate];
            this.multiSelectDatesFlag = this.invOverride.multiSelectDatesFlag;
            this.minSelectableDate = DateUtils.convertToDate(this.invOverride.minSelectableDate);
            this.maxSelectableDate = DateUtils.convertToDate(this.invOverride.maxSelectableDate);
        }, error => {
            console.error(`Error when getting override info ${error}`);
            this.dialog.close();
        });
    }

    private saveApprovals(sendToRars = false): void {
        this.loading = true;
        this.updateRequest();
        this.invOverride.buckets = [];
        this.invOverride.sendToRars = sendToRars;
        if (this.bulkVariableProfileToggle) {
            const variableProfileChangeType = this.variableProfileChangeValueType.split('_')[0];
            const variableProfileChangeValue = this.variableProfileChangeValueType.split('_')[1];
            this.invOverride['variableProfileChangeType'] = variableProfileChangeType;
            this.invOverride['variableProfileChangeValue'] = parseInt(variableProfileChangeValue);
        }
        this.overrideSvc.approveRecommendation(this.invOverride).subscribe(resp => {
            if (resp) {
                this.tryShowUpdateConfirmation(resp);
                this.closeDialog();
            } else {
                this.loading = false;
            }
        }, error => {
            console.error(`Error during bulk approve ${error}`);
            this.loading = false;
        }, () => {
            this.updateComplete.emit(true);
        });
    }

    private saveOverrides(sendToRars = false): void {
        this.loading = true;
        this.invOverride.sendToRars = sendToRars;
        if (this.bulkVariableProfileToggle) {
            const variableProfileChangeType = this.variableProfileChangeValueType.split('_')[0];
            const variableProfileChangeValue = this.variableProfileChangeValueType.split('_')[1];
            this.invOverride['variableProfileChangeType'] = variableProfileChangeType;
            this.invOverride['variableProfileChangeValue'] = parseInt(variableProfileChangeValue);
        }
        if (this.bulkStaticProfileToggle && this.selectedProfile) {
            this.invOverride.overrideProfile = this.selectedProfile.id;
        }
        this.updateRequest();
        this.overrideSvc.saveBulkOverride(this.invOverride).subscribe(resp => {
            if (resp) {
                this.tryShowUpdateConfirmation(resp);
                this.closeDialog();
            } else {
                this.loading = false;
            }
        }, error => {
            console.error(`Error during bulk save ${error}`);
            this.loading = false;
        }, () => {
            this.updateComplete.emit(true);
        });
    }

    private tryShowUpdateConfirmation(resp: any): void {
        try {
            this.showUpdateConfirmation(resp);
        } catch (notifyError) {
            console.error(`Error showing bulk override confirmation ${notifyError}`);
        }
    }

    private showUpdateConfirmation(resp: any): void {
        if (!this.notifyContainer || !resp?.updatedRsids || !resp?.updatedDates) {
            return;
        }
        const rsidCount = resp.updatedRsids.length;
        const dateCount = resp.updatedDates.length;
        const message = `Override applied to ${rsidCount} RSID${rsidCount === 1 ? '' : 's'} `
            + `across ${dateCount} date${dateCount === 1 ? '' : 's'} `
            + `(${resp.updatedCombinationCount} combination${resp.updatedCombinationCount === 1 ? '' : 's'} updated).`;
        const send = this.describeSend(resp?.rarsSend);
        this.notificationSvc.displayNotification(this.notifyContainer, {
            notificationType: send.failed ? 'warning' : 'success',
            message: message + send.text
        });
    }

    /**
     * How the queued RARS send went. The overrides are saved either way, so a send that
     * did not queue must downgrade the toast to a warning rather than be swallowed —
     * otherwise the user walks away believing the journeys are on their way.
     *
     * journeyCount is the same upper bound the confirmation quoted: not every RSID runs
     * on every selected date, and journeys that do not exist never reach the payload.
     */
    private describeSend(rarsSend: any): {text: string, failed: boolean} {
        if (!rarsSend) {
            return {text: '', failed: false};
        }
        if (rarsSend.status === 'Requested') {
            const count = rarsSend.journeyCount;
            return {
                text: ` Up to ${count} journey${count === 1 ? '' : 's'} queued to send to RARS.`,
                failed: false
            };
        }
        if (rarsSend.status === 'Sent') {
            // already done, not merely queued: the count is exact and the server's note
            // is bookkeeping rather than a result
            const count = rarsSend.journeyCount;
            return {
                text: ` ${count} journey${count === 1 ? '' : 's'} sent to RARS.`,
                failed: false
            };
        }
        const reason = rarsSend.note ? ` (${rarsSend.note})` : '';
        return {
            text: ` Send to RARS could not be queued${reason} — retry from Review & Send.`,
            failed: true
        };
    }

    private selectedProfileChange(profile: any): void {
        this.selectedProfile = profile;
    }

    private variableProfileChange(type: any): void {
        this.variableProfileChangeValueType = type;
    }

    private updateRequest(): void {
        this.invOverride.additionalFilters = this.bulkRequest.additionalFilters;
        if (this.bulkRequest.overrideLevel === OverrideLevel.Leg) {
            this.invOverride.legOrigin = this.bulkRequest.journeyOrigin[0];
            this.invOverride.legDestination = this.bulkRequest.journeyDestination[0];
        }
    }

    public nestBuckets() {
        const bucketLength = this.invOverride.buckets.length - 1;
        console.log(this.invOverride.buckets);
        let highest = 0;
        for (let i = bucketLength; i > 0; i--) {
            const bucket = this.invOverride.buckets[i];
            if (bucket.override === null) {
                    bucket.override = bucket.override;
                    highest = bucket.override;
            }
            this.invOverride.buckets[i] = bucket;
            const nextBucketUp = this.invOverride.buckets[i - 1];
            if (highest > bucket.override) {
                nextBucketUp.override = highest;
            } else if (nextBucketUp.override > bucket.override) {
                    highest = nextBucketUp.override;
            } else {
                nextBucketUp.override = bucket.override;
                highest = bucket.override;
            }
            this.invOverride.buckets[i - 1] = nextBucketUp;
        }
        // Scanning up buckets to check if the bucket overrides are valid
        for (let i = bucketLength; i > 0; i--) {
            const bucket = this.invOverride.buckets[i];
            const nextBucketUp = this.invOverride.buckets[i - 1];
            if (bucket.override <= nextBucketUp.override) {
                nextBucketUp.isValid = true;
                bucket.isValid = true;
            }
            this.invOverride.buckets[i] = bucket;
        }
    }
}
